import moment from 'moment';

const hasAddress = (place) => Boolean(place && place.city && place.street1);

/**
 * Turns a year of the employee's own records into the per-worksite trip counts
 * the annual tax report prints.
 *
 * A commute is counted for the worksite the day started at and the one it ended
 * at, so a day spent at a single site scores two — out and back. Days at a site
 * with no street address are left out entirely, because a distance cannot be
 * computed for them and a row with no kilometres is not something to file.
 *
 * REST records carry ids where the GraphQL ones carried embedded objects, so
 * the resource and project are resolved here rather than read off the record.
 */
export const buildEmployeeYearTripData = ({
  records,
  resources,
  projectsById,
  employerId,
}) => {
  const resourcesById = new Map(
    resources.map((resource) => [resource.id, resource])
  );

  // The endpoint returns only the caller's own resources, so employer is the
  // only filter left of the two the report used to apply.
  const employerRecords = records.filter((record) => {
    const resource = resourcesById.get(record.resource);
    return resource && resource.employer === employerId;
  });

  const recordsByDay = new Map();

  employerRecords.forEach((record) => {
    const project = projectsById[record.project];

    if (!hasAddress(project)) {
      return;
    }

    const day = record.clockInAt.substring(0, 10);

    if (!recordsByDay.has(day)) {
      recordsByDay.set(day, []);
    }

    recordsByDay.get(day).push({ ...record, project });
  });

  const projectMap = new Map();

  recordsByDay.forEach((dailyRecords) => {
    let firstRecord = dailyRecords[0];
    let lastRecord = dailyRecords[dailyRecords.length - 1];

    dailyRecords.forEach((record) => {
      const { project } = record;

      if (!projectMap.has(project.id)) {
        projectMap.set(project.id, {
          records: [],
          id: project.id,
          name: project.name,
          city: project.city,
          street1: project.street1,
          street2: project.street2,
          postcode: project.postcode,
          recordDayAmount: 0,
          projectTripAmount: 0,
        });
      }

      projectMap.get(project.id).records.push(record);

      if (moment(record.clockInAt).isBefore(moment(firstRecord.clockInAt))) {
        firstRecord = record;
      }

      if (moment(record.clockOutAt).isAfter(moment(lastRecord.clockOutAt))) {
        lastRecord = record;
      }
    });

    projectMap.get(firstRecord.project.id).projectTripAmount += 1;
    projectMap.get(lastRecord.project.id).projectTripAmount += 1;
  });

  return {
    projects: Array.from(projectMap.values()),
    reportDaysSum: recordsByDay.size,
    // Reported separately so the caller can tell "you logged no hours for this
    // employer" apart from "none of the sites you worked has an address" - the
    // Lambda had two messages for these and the first port collapsed them.
    employerRecordCount: employerRecords.length,
  };
};

/**
 * The address the report prints as the employee's home, and the origin every
 * distance is measured from: the most recently updated resource carrying one.
 */
export const findResourceWithAddress = (resources) => {
  const withAddress = resources.filter(
    (resource) => resource.city || resource.street1 || resource.street2
  );

  if (withAddress.length === 0) {
    return null;
  }

  return withAddress.reduce((previous, current) =>
    previous.updatedAt > current.updatedAt ? previous : current
  );
};

/**
 * Folds the proxy's distances back onto the worksites, in the order they were
 * sent. Distances are per single trip, so each is multiplied by that site's
 * commute count.
 */
export const applyDistances = (projects, distances) => {
  let reportTripSum = 0;
  let reportDistanceSum = 0;

  const recordDistanceMap = projects.map((project, index) => {
    const { distanceString, distanceValue } = distances[index] || {
      distanceString: '-',
      distanceValue: 0,
    };

    const projectDistance =
      (distanceValue * project.projectTripAmount) / 1000;

    reportTripSum += project.projectTripAmount;
    reportDistanceSum += projectDistance;

    return {
      ...project,
      distanceString,
      distanceSum: `${projectDistance.toFixed(0)} km`,
    };
  });

  return {
    recordDistanceMap,
    reportTripSum,
    reportDistanceSum: reportDistanceSum.toFixed(0),
  };
};
